/**
 * 大会本部のパイバル（docs/08 §198）の円形チャート（SVG）。
 *
 * 「上昇しながら流される」チャート（流跡）。大会公式のウェザーシートの
 * WIND CHART と同じ考え方（利用者の指定、2026-10-10）:
 * - 十字の中心がスタート地点（CLP）。一定の速さで上昇しながら、各高度の層
 *   （0〜100m は「100m」の行の風、100〜200m は「200m」の行…）でその風に
 *   流された距離を順に積み上げる
 * - 上昇率の既定は 100m/分（＝1層1分）。公式シートの 2026-10-10 3:55 の表から
 *   この上昇率で描くと、終点（北東へ約1.7km）が公式の図とほぼ一致した
 * - 同心円は距離（m）
 *
 * 見た目は WindView と変える（利用者の指定）: GV4Dの暗いシートに合わせた
 * 配色、点と線は地図の航跡と同じ高度の色分け、同じまとまり（午前・午後）の
 * 前の回も同じ色で細く重ね、古いほど薄くして時間の変化を見せる。
 *
 * DOMに依存しない文字列を返す（テストしやすく、描き直しも単純）。
 */

import { KNOT_MPS, type OfficialPibal } from '@gv4d/core';

export type PibalSpeedUnit = 'kt' | 'mps';

/** 上昇率の選択肢 [m/分] */
export const CLIMB_RATES_MPM = [50, 100, 150, 200, 300] as const;
export const DEFAULT_CLIMB_RATE_MPM = 100;

export interface PibalChartOptions {
  /** 太く描く回（選んでいる回） */
  current: OfficialPibal;
  /** 薄く重ねる前の回（新しい順） */
  previous: OfficialPibal[];
  /** 上昇率 [m/分] */
  climbRateMpm: number;
  /** 高度（CLPからの対地 [m]）→ 色 */
  colorFor: (altAglM: number) => string;
  /** 強調する高度（表の行をタップしたとき） */
  highlightAltM?: number | null;
  /** 1辺の大きさ（viewBox） */
  size?: number;
}

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** 重ねる前の回の数 */
export const PIBAL_HISTORY_MAX = 6;

/** 前の回の濃さ。直前の回がいちばん濃く、古くなるほど薄く（0=直前） */
export function historyOpacity(age: number): number {
  return Math.round(Math.max(0.1, 0.6 * Math.pow(0.72, age)) * 100) / 100;
}

/** 流される先の向き（北=0、時計回り） */
export function downwindBearing(dirFromDeg: number): number {
  return (dirFromDeg + 180) % 360;
}

export function speedIn(unit: PibalSpeedUnit, speedKt: number): number {
  return unit === 'kt' ? speedKt : speedKt * KNOT_MPS;
}

export interface TrajectoryPoint {
  /** 高度（CLPから）[m] */
  altAglM: number;
  /** スタート地点からの東・北 [m] */
  eastM: number;
  northM: number;
  /** スタートからの時間 [秒] */
  tSec: number;
}

/**
 * 上昇しながら流される道すじ。最初の点はスタート地点（高度0）。
 * 各高度の行の風は、その下の層（前の行の高度〜その高度）に当てはめる
 * （公式シートの「0-100m」「100-200m」…と同じ）。無風（calm）は流されない
 */
export function pibalTrajectory(p: OfficialPibal, climbRateMpm: number): TrajectoryPoint[] {
  const rateMps = Math.max(climbRateMpm, 1) / 60;
  const out: TrajectoryPoint[] = [{ altAglM: 0, eastM: 0, northM: 0, tSec: 0 }];
  let prev = out[0];
  for (const l of p.levels) {
    const dt = Math.max(l.altAglM - prev.altAglM, 0) / rateMps;
    const v = l.speedKt * KNOT_MPS;
    const b = l.dirFromDeg === null ? 0 : toRad(downwindBearing(l.dirFromDeg));
    const d = l.dirFromDeg === null ? 0 : v * dt;
    const next = { altAglM: l.altAglM, eastM: prev.eastM + d * Math.sin(b), northM: prev.northM + d * Math.cos(b), tSec: prev.tSec + dt };
    out.push(next);
    prev = next;
  }
  return out;
}

/** 同心円の刻み [m]。いちばん遠い点が収まるきりのよい値を4本くらい */
export function distanceRingStep(maxDistM: number): { step: number; rings: number } {
  const candidates = [50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000, 20000];
  const target = Math.max(maxDistM, 200);
  const step = candidates.find((c) => c * 4 >= target) ?? candidates[candidates.length - 1];
  return { step, rings: Math.max(2, Math.ceil(target / step)) };
}

/** 距離の表示（1000m以上はkm） */
export function formatDistance(m: number): string {
  return m >= 1000 ? `${Math.round(m / 100) / 10}km` : `${Math.round(m)}m`;
}

const fmt = (n: number) => (Math.round(n * 10) / 10).toString();

function escapeXml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}

export function pibalChartSvg(o: PibalChartOptions): string {
  const size = o.size ?? 320;
  const c = size / 2;
  const radius = c - 26;
  const current = pibalTrajectory(o.current, o.climbRateMpm);
  // 同心円の大きさは選んでいる回に合わせる（前の回の遠い点に引っぱられて
  // 今の回が中心に縮こまらないように）。はみ出す前の回の線は円の外を切る
  const maxDist = Math.max(0, ...current.map((t) => Math.hypot(t.eastM, t.northM)));
  const { step, rings } = distanceRingStep(maxDist);
  const scale = radius / (step * rings);
  const xy = (t: TrajectoryPoint): [number, number] => [c + t.eastM * scale, c - t.northM * scale];

  const parts: string[] = [];
  const noteH = 16;
  parts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size + noteH}" class="pibal-chart" role="img" aria-label="上昇しながら流される道すじ（パイバル）">`);
  parts.push(`<defs><clipPath id="pc-clip"><circle cx="${c}" cy="${c}" r="${radius}"/></clipPath></defs>`);
  // 下地: 同心円（距離）と放射線（30度おき、東西南北は少し濃く）
  parts.push(`<circle cx="${c}" cy="${c}" r="${radius}" class="pc-face"/>`);
  for (let i = 1; i <= rings; i++) {
    parts.push(`<circle cx="${c}" cy="${c}" r="${fmt(i * step * scale)}" class="${i === rings ? 'pc-ring pc-ring-outer' : 'pc-ring'}"/>`);
  }
  for (let deg = 0; deg < 360; deg += 30) {
    const major = deg % 90 === 0;
    const x = c + radius * Math.sin(toRad(deg));
    const y = c - radius * Math.cos(toRad(deg));
    parts.push(`<line x1="${c}" y1="${c}" x2="${fmt(x)}" y2="${fmt(y)}" class="${major ? 'pc-spoke pc-spoke-major' : 'pc-spoke'}"/>`);
  }
  for (const [label, deg] of [['N', 0], ['E', 90], ['S', 180], ['W', 270]] as const) {
    const x = c + (radius + 13) * Math.sin(toRad(deg));
    const y = c - (radius + 13) * Math.cos(toRad(deg));
    parts.push(`<text x="${fmt(x)}" y="${fmt(y)}" class="pc-compass" text-anchor="middle" dominant-baseline="central">${label}</text>`);
  }
  // 距離の目盛り（北東の斜めに置く）
  for (let i = 1; i <= rings; i++) {
    const r = i * step * scale;
    const x = c + r * Math.sin(toRad(45));
    const y = c - r * Math.cos(toRad(45));
    parts.push(`<text x="${fmt(x + 2)}" y="${fmt(y - 2)}" class="pc-ring-label">${formatDistance(i * step)}</text>`);
  }

  const segments = (traj: TrajectoryPoint[]) =>
    traj.slice(1).map((t, i) => ({ from: xy(traj[i]), to: xy(t), color: o.colorFor((traj[i].altAglM + t.altAglM) / 2) }));
  const seg = (s: { from: [number, number]; to: [number, number]; color: string }, cls: string) =>
    `<line x1="${fmt(s.from[0])}" y1="${fmt(s.from[1])}" x2="${fmt(s.to[0])}" y2="${fmt(s.to[1])}" class="${cls}" stroke="${s.color}"/>`;

  // 前の回（履歴）: 同じ高度の色で細く、古いほど薄く。古い方から描き、
  // 線の先（いちばん上の高度）に放球時刻を添える（重なるものは新しい方を残す）
  const history = o.previous
    .slice(0, PIBAL_HISTORY_MAX)
    .map((p) => ({ p, segs: segments(pibalTrajectory(p, o.climbRateMpm)), showTime: false }));
  const timeAt: Array<[number, number]> = [];
  for (const h of history) {
    if (h.segs.length === 0) continue;
    const tip = h.segs[h.segs.length - 1].to;
    if (timeAt.every(([x, y]) => Math.hypot(x - tip[0], y - tip[1]) >= 26)) {
      timeAt.push(tip);
      h.showTime = true;
    }
  }
  [...history].reverse().forEach((h) => {
    if (h.segs.length === 0) return;
    parts.push(`<g class="pc-prev" opacity="${historyOpacity(history.indexOf(h))}" clip-path="url(#pc-clip)">`);
    for (const s of h.segs) parts.push(seg(s, 'pc-prev-path'));
    if (h.showTime) {
      const tip = h.segs[h.segs.length - 1].to;
      const time = (h.p.releasedLocal ?? h.p.sentLocal).slice(11, 16);
      parts.push(`<text x="${fmt(tip[0] + 4)}" y="${fmt(tip[1] + 10)}" class="pc-prev-label">${escapeXml(time)}</text>`);
    }
    parts.push('</g>');
  });

  // 選んでいる回: 太い線＋各層の終わりの点＋高度の数字（100m単位）
  for (const s of segments(current)) parts.push(seg(s, 'pc-path'));
  const hi = o.highlightAltM ?? null;
  // 高度の数字は、近くに既に数字があれば省く（弱い風で点が集まると重なるため）。
  // いちばん下・いちばん上・強調した高度は必ず出す
  const labeled: Array<[number, number]> = [];
  current.slice(1).forEach((t, i, arr) => {
    const [x, y] = xy(t);
    const color = o.colorFor(t.altAglM);
    const isHi = hi !== null && Math.abs(t.altAglM - hi) < 1;
    parts.push(`<circle cx="${fmt(x)}" cy="${fmt(y)}" r="${isHi ? 7 : 4.5}" fill="${color}" class="${isHi ? 'pc-dot pc-dot-hi' : 'pc-dot'}" data-alt="${t.altAglM}"/>`);
    const must = i === 0 || i === arr.length - 1 || isHi;
    if (must || labeled.every(([lx, ly]) => Math.hypot(lx - x, ly - y) >= 13)) {
      labeled.push([x, y]);
      const label = t.altAglM % 100 === 0 ? String(t.altAglM / 100) : fmt(t.altAglM / 100);
      parts.push(`<text x="${fmt(x + 6)}" y="${fmt(y - 6)}" class="pc-alt-label">${escapeXml(label)}</text>`);
    }
  });
  parts.push(`<circle cx="${c}" cy="${c}" r="3.5" class="pc-center"/>`);
  parts.push(
    `<text x="6" y="${size + noteH - 4}" class="pc-note">上昇 ${o.climbRateMpm}m/分で流される道すじ・数字は高度（×100m、CLPから）</text>`,
  );
  parts.push('</svg>');
  return parts.join('');
}
